import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = String(process.env.REACT_APP_SUPABASE_URL || '').trim();
const SUPABASE_ANON_KEY = String(process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim();

const isSupabaseRealtimeConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

let supabaseClient = null;
const publisherChannels = new Map();

const normalizeSessionCode = (sessionCode) => String(sessionCode || '')
  .trim()
  .toUpperCase()
  .replace(/[^A-Z0-9]/g, '');

const getSessionTopic = (sessionCode) => {
  const normalized = normalizeSessionCode(sessionCode);
  if (!normalized) return '';
  return `guest-session-${normalized}`;
};

const getSupabaseClient = () => {
  if (!isSupabaseRealtimeConfigured) return null;
  if (supabaseClient) return supabaseClient;

  supabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    realtime: {
      params: {
        eventsPerSecond: 15,
      },
    },
  });

  return supabaseClient;
};

const ensurePublisherChannel = async (client, topic) => {
  if (!client || !topic) return null;

  const existing = publisherChannels.get(topic);
  if (existing?.channel && existing?.readyPromise) {
    const ready = await existing.readyPromise;
    return ready ? existing.channel : null;
  }

  const channel = client.channel(`${topic}-publisher`, {
    config: { broadcast: { self: true } },
  });

  let resolveReady;
  const readyPromise = new Promise((resolve) => {
    resolveReady = resolve;
  });

  publisherChannels.set(topic, { channel, readyPromise });

  const timeout = setTimeout(() => {
    resolveReady(false);
  }, 2400);

  channel.subscribe((status) => {
    if (status === 'SUBSCRIBED') {
      clearTimeout(timeout);
      resolveReady(true);
      return;
    }

    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
      clearTimeout(timeout);
      resolveReady(false);
      publisherChannels.delete(topic);
      client.removeChannel(channel).catch(() => null);
    }
  });

  const ready = await readyPromise;
  if (!ready) {
    publisherChannels.delete(topic);
    return null;
  }

  return channel;
};

const subscribeToSessionRealtime = ({
  sessionCode,
  onEvent,
  onStatus,
}) => {
  const client = getSupabaseClient();
  const topic = getSessionTopic(sessionCode);

  if (!client || !topic || typeof onEvent !== 'function') {
    return () => {};
  }

  const channel = client.channel(topic, {
    config: { broadcast: { self: true } },
  });

  channel
    .on('broadcast', { event: 'session-updated' }, ({ payload }) => {
      onEvent('session-updated', payload || {});
    })
    .on('broadcast', { event: 'messages-updated' }, ({ payload }) => {
      onEvent('messages-updated', payload || {});
    })
    .on('broadcast', { event: 'voice-signals-updated' }, ({ payload }) => {
      onEvent('voice-signals-updated', payload || {});
    })
    .subscribe((status) => {
      if (typeof onStatus === 'function') {
        onStatus(status);
      }
    });

  return () => {
    client.removeChannel(channel).catch(() => null);
  };
};

const publishSessionRealtimeEvent = async ({
  sessionCode,
  eventName,
  payload = {},
}) => {
  const client = getSupabaseClient();
  const topic = getSessionTopic(sessionCode);

  if (!client || !topic || !eventName) {
    return false;
  }

  const channel = await ensurePublisherChannel(client, topic);
  if (!channel) return false;

  let ok = false;
  try {
    const result = await channel.send({
      type: 'broadcast',
      event: eventName,
      payload: {
        ...payload,
        emittedAt: new Date().toISOString(),
      },
    });
    ok = result === 'ok';
  } catch {
    ok = false;
  }

  return ok;
};

export {
  isSupabaseRealtimeConfigured,
  subscribeToSessionRealtime,
  publishSessionRealtimeEvent,
};
